// Stream helper for SSE-based AI responses from our edge functions
import { supabase } from "@/integrations/supabase/client";

export type ChatRole = "user" | "assistant" | "system";
export type ChatMsg = { role: ChatRole; content: string };

export type Usage = {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  completion_tokens_details?: {
    reasoning_tokens?: number;
  };
};

export async function streamSSE({
  url,
  body,
  onDelta,
  onMeta,
  onUsage,
  onEvent,
  signal,
  extraHeaders,
}: {
  url: string;
  body: unknown;
  onDelta: (chunk: string) => void;
  onMeta?: (meta: unknown) => void;
  onUsage?: (usage: Usage) => void;
  /**
   * Generic SSE event handler. Called for every named event other than
   * the OpenAI-style token stream (which goes to onDelta). Used by
   * agentic loops that emit `event: step / tool_call / tool_result /
   * final / error` frames alongside `event: token`. The legacy `meta`
   * event is still routed to `onMeta` for compatibility.
   */
  onEvent?: (event: string, data: unknown) => void;
  signal?: AbortSignal;
  /** Caller-supplied request headers (e.g. guest tracking flags). */
  extraHeaders?: Record<string, string>;
}): Promise<void> {
  // Use the signed-in user's JWT as Bearer so edge functions can identify
  // the caller via getClaims(). The publishable/anon key still goes in the
  // `apikey` header (required by Supabase's edge runtime to route the request).
  //
  // RACE GUARD: Supabase's `getSession()` can return `null` immediately after
  // page load while the SDK is still rehydrating the session from
  // localStorage. If a chat is sent in those first few hundred ms (e.g.
  // auto-send from a seeded composer, or the user lands and clicks fast),
  // we'd silently fall back to the anon key — and any auth-required edge
  // function (hub-chat, tutor-chat, recommend-chat, …) would respond
  // "Not authenticated". To prevent that, we briefly poll for the session
  // before deciding to fall back. We also detect endpoints that explicitly
  // allow anonymous traffic (`home-demo-chat`) and skip the wait for them.
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
  const isPublicEndpoint = /\/functions\/v1\/(home-demo-chat)\b/.test(url);

  const session = await waitForSession({ timeoutMs: isPublicEndpoint ? 0 : 1500, signal });
  if (!session && !isPublicEndpoint) {
    const err = new Error(
      "You're signed out — please sign in to use chat.",
    ) as Error & { status?: number; code?: string };
    err.status = 401;
    err.code = "NOT_AUTHENTICATED";
    throw err;
  }
  const bearer = session?.access_token || anon;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
      apikey: anon,
      ...extraHeaders,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    let msg = "Request failed";
    try {
      const parsed = JSON.parse(text);
      msg = parsed.error || msg;
    } catch {
      msg = text || msg;
    }
    // If the server still says "Not authenticated" despite us having waited
    // for the session, surface a friendlier message instead of leaking the
    // raw server string. This typically means the JWT expired between
    // getSession() and the fetch; refreshSession() and a single retry
    // recover transparently.
    if (resp.status === 401 && /not authenticated/i.test(msg) && !isPublicEndpoint) {
      const refreshed = await supabase.auth.refreshSession().catch(() => null);
      const newToken = refreshed?.data?.session?.access_token;
      if (newToken) {
        const retry = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${newToken}`,
            apikey: anon,
            ...extraHeaders,
          },
          body: JSON.stringify(body),
          signal,
        });
        if (retry.ok && retry.body) {
          await consumeSSE(retry.body, { onDelta, onMeta, onUsage, onEvent });
          return;
        }
      }
      const err = new Error(
        "Your session expired — please sign in again.",
      ) as Error & { status?: number; code?: string };
      err.status = 401;
      err.code = "NOT_AUTHENTICATED";
      throw err;
    }
    const err = new Error(msg) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  if (!resp.body) throw new Error("No response stream");

  await consumeSSE(resp.body, { onDelta, onMeta, onUsage, onEvent });
}

// Polls `supabase.auth.getSession()` for up to `timeoutMs` so callers
// don't race the SDK's storage rehydration on first paint. Returns the
// session (or null on timeout). 50ms cadence is short enough to feel
// instant; 1.5s ceiling is long enough to cover slow mobile rehydrate
// without making a genuine "signed out" state feel broken.
async function waitForSession(opts: {
  timeoutMs: number;
  signal?: AbortSignal;
}): Promise<Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"]> {
  const { timeoutMs, signal } = opts;
  const deadline = Date.now() + timeoutMs;
  // First read — covers the common case where the session is already there.
  let { data: { session } } = await supabase.auth.getSession();
  if (session || timeoutMs <= 0) return session;
  while (!session && Date.now() < deadline) {
    if (signal?.aborted) return null;
    await new Promise((r) => setTimeout(r, 50));
    ({ data: { session } } = await supabase.auth.getSession());
  }
  return session ?? null;
}

// Extracted SSE reader so we can reuse it from the 401-retry path.
async function consumeSSE(
  body: ReadableStream<Uint8Array>,
  handlers: {
    onDelta: (chunk: string) => void;
    onMeta?: (meta: unknown) => void;
    onUsage?: (usage: Usage) => void;
    onEvent?: (event: string, data: unknown) => void;
  },
): Promise<void> {
  const { onDelta, onMeta, onUsage, onEvent } = handlers;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = false;
  let currentEvent = "";

  while (!done) {
    const { done: isDone, value } = await reader.read();
    if (isDone) break;
    buffer += decoder.decode(value, { stream: true });

    let nl: number;
    while ((nl = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line) {
        currentEvent = "";
        continue;
      }
      if (line.startsWith(":")) continue;
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
        continue;
      }
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") {
        done = true;
        break;
      }
      try {
        const parsed = JSON.parse(json);
        if (currentEvent === "meta") {
          onMeta?.(parsed);
          currentEvent = "";
          continue;
        }
        // Agent-style streams emit named events ("step", "tool_call",
        // "tool_result", "final", "error", "token"). Forward everything
        // that isn't a vanilla OpenAI delta to onEvent so callers can
        // render structured traces. The "token" event still falls
        // through to onDelta below since we treat token frames as the
        // canonical text stream regardless of how the server labels them.
        if (currentEvent && currentEvent !== "token") {
          onEvent?.(currentEvent, parsed);
          currentEvent = "";
          continue;
        }
        // `event: token` whose data is a bare JSON string ("hello world").
        if (currentEvent === "token" && typeof parsed === "string") {
          onDelta(parsed);
          currentEvent = "";
          continue;
        }
        // Final usage chunk: OpenAI-compatible streams emit a chunk
        // with empty choices[] and a populated `usage` object.
        if (parsed.usage && (!parsed.choices || parsed.choices.length === 0)) {
          onUsage?.(parsed.usage as Usage);
          continue;
        }
        const delta = parsed.choices?.[0]?.delta?.content;
        if (delta) onDelta(delta);
      } catch {
        // partial JSON; put back
        buffer = (currentEvent ? `event: ${currentEvent}\n` : "") + line + "\n" + buffer;
        break;
      }
    }
  }
}
